import React, { useEffect, useState } from 'react';
import { Plus, ArrowLeft, Send, Archive, RotateCcw, Trash2, Loader2 } from 'lucide-react';
import { useApi, useUserContext } from './DashboardLayout';

const STATUS_MAP = { offen: { label: 'Offen', bg: '#dbeafe', color: '#1d4ed8' }, in_arbeit: { label: 'In Arbeit', bg: '#fef3c7', color: '#92400e' }, erledigt: { label: 'Erledigt', bg: '#d1fae5', color: '#065f46' } };
const PRIO_MAP = { hoch: { label: 'Hoch', bg: '#fee2e2', color: '#991b1b' }, mittel: { label: 'Mittel', bg: '#fef3c7', color: '#92400e' }, niedrig: { label: 'Niedrig', bg: '#d1fae5', color: '#065f46' } };

function Badge({ map, value }) {
  const s = map[value] || { label: value, bg: '#f3f4f6', color: '#374151' };
  return <span data-testid={`badge-${value}`} style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: s.bg, color: s.color, whiteSpace: 'nowrap' }}>{s.label}</span>;
}

function TicketList({ tickets, onSelect, onCreate, showArchived, setShowArchived, userType }) {
  const [search, setSearch] = useState('');
  const filtered = tickets.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [t.title, t.created_by_name, t.provider_name].some(v => (v || '').toLowerCase().includes(q));
  });

  return (
    <div data-testid="ticket-list">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2>Tickets</h2>
          <p>{showArchived ? 'Archivierte Tickets' : `${tickets.length} Ticket${tickets.length !== 1 ? 's' : ''}`}</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={() => setShowArchived(!showArchived)} data-testid="toggle-archive-btn"
            style={{ background: 'var(--page-bg)', border: '1px solid var(--border)', fontSize: 13 }}>
            {showArchived ? <><RotateCcw size={14} /> Aktive</> : <><Archive size={14} /> Archiv</>}
          </button>
          <button className="btn btn-primary" onClick={onCreate} data-testid="create-ticket-btn">
            <Plus size={16} /> Neues Ticket
          </button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '0.75rem' }}>
        <div className="card-body" style={{ padding: '0.5rem 1rem' }}>
          <input className="form-input" placeholder="Tickets durchsuchen..." value={search} onChange={e => setSearch(e.target.value)} data-testid="ticket-search"
            style={{ border: 'none', boxShadow: 'none', padding: '4px 0' }} />
        </div>
      </div>

      <div className="card">
        {filtered.length === 0 ? (
          <div className="empty-state"><p>{showArchived ? 'Keine archivierten Tickets' : 'Keine Tickets vorhanden'}</p></div>
        ) : (
          <div className="table-wrap">
            <table data-testid="tickets-table">
              <thead>
                <tr>
                  <th style={{ width: 50 }}>#</th>
                  <th>Titel</th>
                  {userType !== 3 && <th style={{ width: '18%' }}>Erstellt von</th>}
                  <th style={{ width: 100 }}>Priorität</th>
                  <th style={{ width: 100 }}>Status</th>
                  <th style={{ width: 100 }}>Datum</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(t => (
                  <tr key={t.id} onClick={() => onSelect(t.id)} style={{ cursor: 'pointer' }} data-testid={`ticket-row-${t.id}`}>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12, fontFamily: 'monospace' }}>{t.id}</td>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{t.title}</div>
                      {t.comments?.length > 0 && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t.comments.length} Kommentar{t.comments.length > 1 ? 'e' : ''}</span>}
                    </td>
                    {userType !== 3 && (
                      <td>
                        <div style={{ fontSize: 12 }}>{t.created_by_name}</div>
                        {t.provider_name && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t.created_by_email}</div>}
                      </td>
                    )}
                    <td><Badge map={PRIO_MAP} value={t.priority} /></td>
                    <td><Badge map={STATUS_MAP} value={t.status} /></td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function TicketForm({ onBack, apiFetch, onCreated }) {
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [priority, setPriority] = useState('mittel');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const r = await apiFetch('/api/tickets', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title, text, priority }),
    });
    const d = await r?.json();
    if (d?.status) onCreated(d.ticket);
    setSaving(false);
  };

  return (
    <div data-testid="ticket-form">
      <div className="page-header">
        <button className="btn" onClick={onBack} style={{ background: 'none', border: 'none', padding: 0, marginBottom: 8, fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
          <ArrowLeft size={14} /> Zurück zur Liste
        </button>
        <h2>Neues Ticket</h2>
      </div>
      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.75rem 1.5rem' }}>
              <div className="form-group" style={{ gridColumn: '1' }}>
                <label className="form-label">Titel</label>
                <input className="form-input" value={title} onChange={e => setTitle(e.target.value)} required placeholder="Kurze Beschreibung des Anliegens" data-testid="ticket-title-input" />
              </div>
              <div className="form-group">
                <label className="form-label">Priorität</label>
                <select className="form-input" value={priority} onChange={e => setPriority(e.target.value)} data-testid="ticket-priority-select">
                  <option value="niedrig">Niedrig</option>
                  <option value="mittel">Mittel</option>
                  <option value="hoch">Hoch</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Beschreibung</label>
              <textarea className="form-input" value={text} onChange={e => setText(e.target.value)} required rows={6} placeholder="Beschreiben Sie Ihr Anliegen im Detail..." data-testid="ticket-text-input" style={{ resize: 'vertical' }} />
            </div>
            <button type="submit" className="btn btn-primary" disabled={saving} data-testid="ticket-submit-btn">
              {saving ? <><Loader2 size={16} className="spin" /> Erstellen...</> : <><Plus size={16} /> Ticket erstellen</>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function TicketDetail({ ticketId, onBack, apiFetch, userType }) {
  const [ticket, setTicket] = useState(null);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => { loadTicket(); }, [ticketId]);

  const loadTicket = async () => {
    const r = await apiFetch(`/api/tickets/${ticketId}`);
    const d = await r?.json();
    if (d) setTicket(d);
  };

  const updateField = async (field, value) => {
    await apiFetch(`/api/tickets/${ticketId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [field]: value }),
    });
    loadTicket();
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setSending(true);
    await apiFetch(`/api/tickets/${ticketId}/comments`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: comment }),
    });
    setComment('');
    setSending(false);
    loadTicket();
  };

  const archiveTicket = async () => {
    await apiFetch(`/api/tickets/${ticketId}/archive`, { method: 'POST' });
    onBack();
  };

  const restoreTicket = async () => {
    await apiFetch(`/api/tickets/${ticketId}/restore`, { method: 'POST' });
    loadTicket();
  };

  const deleteTicket = async () => {
    if (!window.confirm('Ticket endgültig löschen?')) return;
    await apiFetch(`/api/tickets/${ticketId}`, { method: 'DELETE' });
    onBack();
  };

  if (!ticket) return <div className="empty-state"><Loader2 size={24} className="spin" /></div>;

  const isAdmin = userType === 1 || userType === 2;

  return (
    <div data-testid="ticket-detail">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <button className="btn" onClick={onBack} style={{ background: 'none', border: 'none', padding: 0, marginBottom: 8, fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
            <ArrowLeft size={14} /> Zurück zur Liste
          </button>
          <h2>Ticket #{ticket.id}: {ticket.title}</h2>
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', gap: 8 }}>
            {ticket.archived ? (
              <button className="btn" onClick={restoreTicket} data-testid="ticket-restore-btn" style={{ background: 'var(--info)', color: '#fff', border: 'none', fontSize: 13 }}>
                <RotateCcw size={14} /> Wiederherstellen
              </button>
            ) : ticket.status === 'erledigt' ? (
              <button className="btn" onClick={archiveTicket} data-testid="ticket-archive-btn" style={{ background: 'var(--warning)', color: '#fff', border: 'none', fontSize: 13 }}>
                <Archive size={14} /> Archivieren
              </button>
            ) : null}
            <button className="btn" onClick={deleteTicket} data-testid="ticket-delete-btn" style={{ background: 'var(--error)', color: '#fff', border: 'none', fontSize: 13 }}>
              <Trash2 size={14} /> Löschen
            </button>
          </div>
        )}
      </div>

      {/* Ticket info card */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Status</div>
              {isAdmin ? (
                <select className="form-input" value={ticket.status} onChange={e => updateField('status', e.target.value)} data-testid="ticket-status-select" style={{ fontSize: 13, padding: '4px 8px' }}>
                  <option value="offen">Offen</option>
                  <option value="in_arbeit">In Arbeit</option>
                  <option value="erledigt">Erledigt</option>
                </select>
              ) : <Badge map={STATUS_MAP} value={ticket.status} />}
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Priorität</div>
              {isAdmin ? (
                <select className="form-input" value={ticket.priority} onChange={e => updateField('priority', e.target.value)} data-testid="ticket-priority-change" style={{ fontSize: 13, padding: '4px 8px' }}>
                  <option value="niedrig">Niedrig</option>
                  <option value="mittel">Mittel</option>
                  <option value="hoch">Hoch</option>
                </select>
              ) : <Badge map={PRIO_MAP} value={ticket.priority} />}
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Erstellt am</div>
              <div style={{ fontSize: 13 }}>{new Date(ticket.created_at).toLocaleString('de-DE')}</div>
            </div>
          </div>

          {/* Ersteller-Info */}
          <div style={{ padding: '0.75rem 1rem', background: 'var(--page-bg)', borderRadius: 8, marginBottom: '1rem', fontSize: 13 }}>
            <strong>Erstellt von:</strong> {ticket.created_by_name}
            {ticket.provider_name && <span style={{ color: 'var(--text-muted)' }}> — {ticket.provider_name}</span>}
            {ticket.created_by_email && <span style={{ color: 'var(--text-muted)' }}> ({ticket.created_by_email})</span>}
          </div>

          {/* Ticket text */}
          <div style={{ padding: '1rem', background: '#fff', borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
            {ticket.text}
          </div>
        </div>
      </div>

      {/* Kommentare */}
      <div className="card">
        <div className="card-header" style={{ fontWeight: 600, fontSize: 14 }}>
          Kommentare ({ticket.comments?.length || 0})
        </div>
        <div className="card-body">
          {ticket.comments?.length > 0 ? (
            <div style={{ marginBottom: '1rem' }}>
              {ticket.comments.map(c => (
                <div key={c.id} data-testid={`comment-${c.id}`} style={{ padding: '0.75rem 1rem', marginBottom: 8, background: c.created_by_type === 3 ? 'rgba(59,130,246,0.04)' : 'rgba(16,185,129,0.04)', borderRadius: 8, borderLeft: `3px solid ${c.created_by_type === 3 ? 'var(--info)' : 'var(--success)'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12 }}>
                    <strong>{c.created_by_name}</strong>
                    <span style={{ color: 'var(--text-muted)' }}>{new Date(c.created_at).toLocaleString('de-DE')}</span>
                  </div>
                  <div style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{c.text}</div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: '1rem' }}>Noch keine Kommentare.</p>
          )}

          <form onSubmit={addComment} style={{ display: 'flex', gap: 8 }}>
            <textarea className="form-input" value={comment} onChange={e => setComment(e.target.value)} placeholder="Kommentar schreiben..." rows={2} data-testid="comment-input"
              style={{ flex: 1, resize: 'vertical', fontSize: 13 }} />
            <button type="submit" className="btn btn-primary" disabled={sending || !comment.trim()} data-testid="comment-submit-btn"
              style={{ alignSelf: 'flex-end' }}>
              {sending ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function TicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [view, setView] = useState('list'); // list | create | detail
  const [selectedId, setSelectedId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const { apiFetch } = useApi();
  const { user } = useUserContext();
  const userType = user?.type || 3;

  useEffect(() => { loadTickets(); }, [showArchived]);

  const loadTickets = async () => {
    const r = await apiFetch(`/api/tickets?archived=${showArchived}`);
    const d = await r?.json();
    if (d) setTickets(d);
  };

  const openTicket = (id) => { setSelectedId(id); setView('detail'); };

  if (view === 'create') {
    return <TicketForm onBack={() => { setView('list'); loadTickets(); }} apiFetch={apiFetch} onCreated={(t) => { setView('detail'); setSelectedId(t.id); loadTickets(); }} />;
  }

  if (view === 'detail' && selectedId) {
    return <TicketDetail ticketId={selectedId} onBack={() => { setView('list'); loadTickets(); }} apiFetch={apiFetch} userType={userType} />;
  }

  return <TicketList tickets={tickets} onSelect={openTicket} onCreate={() => setView('create')} showArchived={showArchived} setShowArchived={setShowArchived} userType={userType} />;
}
